import { useCallback, useEffect, useMemo, useRef } from 'react';

import { TabType } from '@/components/Types';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import {
  ActionTypeManageTDAEnum,
  requestPostulationTda,
  postManagePostulant,
} from '@/services/tda';
import { ActionsTourneys, GenericItemTourneysData } from '@/types/tda';
import { ERROR_STATUS_TDA, TEXTS } from '@/utils';

export const useHandleInvitations = (
  list: GenericItemTourneysData[] | null,
  tab = TabType.FirstTab
) => {
  const toast = useToast();
  const { updateTourney, tourneyData } = useTourney();
  const listRef = useRef<GenericItemTourneysData[] | null>(null);

  // Update the ref when list changes
  useEffect(() => {
    if (!list) return;
    listRef.current = list;
  }, [list]);

  const textComplementary = useMemo(() => {
    return tab === TabType.FirstTab
      ? 'al Torneo de Amigos'
      : 'al Torneo de Amigos de';
  }, [tab]);

  const onHandlePendingPostulation = useCallback(
    async (id: number, name: string) => {
      try {
        const responseManagePostulant = await postManagePostulant({
          actionType: ActionTypeManageTDAEnum.AcceptInvitation,
          idTourney: id,
        });

        if (responseManagePostulant.estado) {
          if (tab === TabType.FirstTab) {
            toast.show(
              `Tenías una invitación pendiente para el torneo ${name}. Ya estás participando.`
            );
          } else {
            toast.show(
              `Tenías una invitación pendiente para el torneo de ${name}. Ya estás participando.`
            );
          }
        } else {
          console.error(responseManagePostulant.mensaje);
          toast.show('Error al invitar');
        }
      } catch (error) {
        console.error(error);
        toast.show(TEXTS.defaultError);
      }
    },
    [tab, toast]
  );

  const onHandleSubmit = useCallback(
    async ({ id }: ActionsTourneys) => {
      try {
        // Check if list is available
        if (!listRef.current) return;

        const tourney = listRef.current.find((t) => t.id === id);
        if (!tourney) return; // Add check for tourney existence

        const response = await requestPostulationTda(id);

        if (response.estado) {
          toast.show(
            `Enviaste tu solicitud para unirte ${textComplementary} ${tourney.title}`
          );
          // set refresh and pending list ids
          updateTourney({
            ...tourneyData,
            idsPendings: [...(tourneyData.idsPendings || []), id],
          });
        } else {
          if (
            response.codigo &&
            response.codigo === ERROR_STATUS_TDA.PENDING_INVITATION
          ) {
            await onHandlePendingPostulation(id, String(tourney.title));

            // set refresh after invitacion
            updateTourney({
              ...tourneyData,
              idsPlayings: [...(tourneyData.idsPlayings || []), id],
            });
          } else if (
            response.mensaje &&
            response.codigo !== ERROR_STATUS_TDA.PENDING_INVITATION
          ) {
            toast.show(response.mensaje);
          }
        }
      } catch (err) {
        console.error(err);
        toast.show(TEXTS.defaultError);
      }
    },
    [
      toast,
      textComplementary,
      updateTourney,
      tourneyData,
      onHandlePendingPostulation,
    ]
  );

  return { onHandleSubmit };
};
